import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  Skeleton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  CircularProgress,
  Checkbox,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TableChart,
  Map as MapIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  Description as CsvIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import { fetchLocationReport, resetReport } from '../../../store/slices/geoLocationSlice';
import { toolsApi, EmployeeOption } from '../../../api/toolsApi';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchOSRMRoute, RouteResult } from '../../../utils/routingUtils';
import type { LocationReportDto } from '../../../types';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createNumberedIcon = (index: number) =>
  L.divIcon({
    className: '',
    html: `<div style="background:#1976d2;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${index}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const ALL_EMPLOYEES_OPTION: EmployeeOption = { id: '__all__', name: 'All Employees' };

const LocationReportPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { report, loading, error } = useSelector((state: RootState) => state.geoLocation);
  const user = useSelector((state: RootState) => state.auth.user);

  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'map' | 'route'>('table');
  const [routeData, setRouteData] = useState<RouteResult | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const location = useLocation();
  const [srNoSortDirection, setSrNoSortDirection] = useState<'asc' | 'desc' | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Search
  const [searchText, setSearchText] = useState('');

  // Export menu
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    toolsApi.getEmployees().then((result) => {
      setEmployees(result || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.userId && employees.length > 0) {
      const currentUser = employees.find((e) => e.id === user.userId);
      if (currentUser) {
        setSelectedEmployees([currentUser]);
      }
    }
  }, [user, employees]);

  useEffect(() => {
    dispatch(resetReport());
  }, [dispatch, location.pathname, user?.userId]);

  const getSelectedIds = (): string[] => {
    const hasAll = selectedEmployees.some((e) => e.id === '__all__');
    if (hasAll) return employees.map((e) => e.id);
    return selectedEmployees.map((e) => e.id);
  };

  const handleFetch = () => {
    const ids = getSelectedIds();
    if (ids.length > 0 && startDate && endDate) {
      dispatch(fetchLocationReport({ employeeIds: ids, startDate, endDate }));
      setPage(0);
    }
  };

  useEffect(() => {
    const ids = getSelectedIds();
    if (ids.length > 0) {
      handleFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployees]);

  // Filtered data based on search
  const filteredReport = useMemo(() => {
    if (!searchText.trim()) return report;
    const lower = searchText.toLowerCase();
    return report.filter(
      (item) =>
        item.employeeName.toLowerCase().includes(lower) ||
        item.locationName.toLowerCase().includes(lower) ||
        (item.source || '').toLowerCase().includes(lower) ||
        (item.mode || '').toLowerCase().includes(lower) ||
        new Date(item.date).toLocaleDateString().includes(lower)
    );
  }, [report, searchText]);

  const sortedReportRows = useMemo(() => {
    const rows = filteredReport.map((item, idx) => ({
      item,
      srNo: idx + 1,
    }));

    if (!srNoSortDirection) {
      return rows;
    }

    return rows.sort((a, b) =>
      srNoSortDirection === 'asc' ? a.srNo - b.srNo : b.srNo - a.srNo
    );
  }, [filteredReport, srNoSortDirection]);

  // Paginated data
  const paginatedReportRows = useMemo(() => {
    return sortedReportRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedReportRows, page, rowsPerPage]);

  const formatDuration = (minutes: number): string => {
    if (minutes === 0) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const totalDistanceKm = report.reduce((sum, item) => sum + (item.distanceKm || 0), 0);
  const displayedDistanceKm = sortedReportRows.reduce((sum, row) => sum + (row.item.distanceKm || 0), 0);
  const validLocations = report.filter((r) => r.latitude !== 0 && r.longitude !== 0);
  const mapCenter: [number, number] = validLocations.length > 0
    ? [validLocations[0].latitude, validLocations[0].longitude]
    : [20.5937, 78.9629];
  const polylinePositions: [number, number][] = validLocations.map((r) => [r.latitude, r.longitude]);

  // Fetch OSRM route when switching to route view
  useEffect(() => {
    if (viewMode !== 'route' || validLocations.length < 2) {
      setRouteData(null);
      return;
    }
    let cancelled = false;
    const loadRoute = async () => {
      setRouteLoading(true);
      const waypoints: [number, number][] = validLocations.map((r) => [r.latitude, r.longitude]);
      const result = await fetchOSRMRoute(waypoints);
      if (!cancelled) {
        setRouteData(result);
        setRouteLoading(false);
      }
    };
    loadRoute();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, report]);

  // Export helpers
  const getExportRows = (data: Array<{ item: LocationReportDto; srNo: number }>) =>
    data.map(({ item, srNo }) => ({
      'Sr. No.': srNo,
      Employee: item.employeeName,
      Date: new Date(item.date).toLocaleDateString(),
      Location: item.locationName,
      Arrival: new Date(item.arrivalTime).toLocaleTimeString(),
      Departure: item.departureTime ? new Date(item.departureTime).toLocaleTimeString() : '-',
      Duration: formatDuration(item.durationMinutes),
      Distance: item.distanceKm > 0 ? `${item.distanceKm} km` : '-',
      Source: item.source || '',
      Mode: item.mode || '',
      Coordinates: `${item.latitude}, ${item.longitude}`,
    }));

  const handleExportExcel = () => {
    const rows = getExportRows(sortedReportRows);
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key]).length)) + 2,
    }));
    ws['!cols'] = colWidths;
    // Style header row
    const headerKeys = Object.keys(rows[0] || {});
    headerKeys.forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: '673AB7' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            bottom: { style: 'thin', color: { rgb: '000000' } },
          },
        };
      }
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Location Report');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), `Location_Report_${startDate}_${endDate}.xlsx`);
    setExportAnchor(null);
  };

  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Location Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Period: ${startDate} to ${endDate}`, 14, 22);
    doc.text(`Total Stops: ${sortedReportRows.length} | Total Distance: ${displayedDistanceKm.toFixed(2)} km`, 14, 28);

    const rows = getExportRows(sortedReportRows);
    const columns = Object.keys(rows[0] || {});

    autoTable(doc, {
      startY: 33,
      head: [columns],
      body: rows.map((r) => columns.map((c) => (r as any)[c])),
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [103, 58, 183], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`Location_Report_${startDate}_${endDate}.pdf`);
    setExportAnchor(null);
  };

  const handleExportCsv = () => {
    const rows = getExportRows(sortedReportRows);
    const columns = Object.keys(rows[0] || {});
    const escapeCsvValue = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      columns.map(escapeCsvValue).join(','),
      ...rows.map((row) => columns.map((column) => escapeCsvValue((row as any)[column])).join(',')),
    ].join('\n');

    saveAs(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      `Location_Report_${startDate}_${endDate}.csv`
    );
    setExportAnchor(null);
  };

  const sourceChipColor = (source: string): 'success' | 'warning' | 'default' => {
    if (source?.includes('Check-In')) return 'success';
    if (source?.includes('Check-Out')) return 'warning';
    return 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Location Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="table"><TableChart sx={{ mr: 0.5 }} /> Table</ToggleButton>
            <ToggleButton value="map"><MapIcon sx={{ mr: 0.5 }} /> Map</ToggleButton>
            <ToggleButton value="route"><RouteIcon sx={{ mr: 0.5 }} /> Route</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={loading || sortedReportRows.length === 0}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={handleExportExcel}>
              <ListItemIcon><ExcelIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>Export Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportCsv}>
              <ListItemIcon><CsvIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText>Export CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportPdf}>
              <ListItemIcon><PdfIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Export PDF</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 350 }}
            options={[ALL_EMPLOYEES_OPTION, ...employees]}
            value={selectedEmployees}
            onChange={(_, newValue) => {
              const hadAll = selectedEmployees.some((e) => e.id === '__all__');
              const hasAll = newValue.some((e) => e.id === '__all__');
              if (hasAll && !hadAll) {
                setSelectedEmployees([ALL_EMPLOYEES_OPTION]);
              } else if (hasAll && newValue.length > 1) {
                setSelectedEmployees(newValue.filter((e) => e.id !== '__all__'));
              } else {
                setSelectedEmployees(newValue);
              }
            }}
            disableCloseOnSelect
            getOptionLabel={(option) =>
              option.id === '__all__'
                ? option.name
                : option.employeeCode
                  ? `${option.name} (${option.employeeCode})`
                  : option.name
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                {option.id === '__all__'
                  ? option.name
                  : option.employeeCode
                    ? `${option.name} (${option.employeeCode})`
                    : option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Employees" placeholder="Select employees..." />
            )}
          />
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={handleFetch} disabled={loading} sx={{ textTransform: 'none' }}>
            Search
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Chip label={`Total Stops: ${report.length}`} color="primary" variant="outlined" />
          <Chip label={`Total Distance (straight): ${totalDistanceKm.toFixed(2)} km`} color="secondary" variant="outlined" />
          {viewMode === 'route' && routeData && (
            <>
              <Chip label={`Road Distance: ${routeData.distanceKm} km`} color="error" variant="outlined" />
              <Chip label={`Est. Travel Time: ${routeData.durationMinutes} min`} color="info" variant="outlined" />
            </>
          )}
        </Paper>
      )}

      {viewMode === 'table' ? (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by employee, location, source, mode..."
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              sx={{ width: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>×</IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.813rem', lineHeight: 1.3 } }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { py: 1, whiteSpace: 'nowrap', backgroundColor: '#f5f5f5' } }}>
                  <TableCell sortDirection={srNoSortDirection || false}>
                    <TableSortLabel
                      active={Boolean(srNoSortDirection)}
                      direction={srNoSortDirection || 'asc'}
                      onClick={() => {
                        setSrNoSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
                        setPage(0);
                      }}
                    >
                      <strong>Sr. No.</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Arrival</strong></TableCell>
                  <TableCell><strong>Departure</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Distance</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Mode</strong></TableCell>
                  <TableCell><strong>Coordinates</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 11 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredReport.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No report data found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReportRows.map(({ item, srNo }) => (
                    <TableRow key={`${item.employeeId}-${item.arrivalTime}-${srNo}`}>
                      <TableCell>{srNo}</TableCell>
                      <TableCell>{item.employeeName}</TableCell>
                      <TableCell>{new Date(item.date).toLocaleDateString()}</TableCell>
                      <TableCell>{item.locationName}</TableCell>
                      <TableCell>{new Date(item.arrivalTime).toLocaleTimeString()}</TableCell>
                      <TableCell>
                        {item.departureTime
                          ? new Date(item.departureTime).toLocaleTimeString()
                          : '-'}
                      </TableCell>
                      <TableCell>{formatDuration(item.durationMinutes)}</TableCell>
                      <TableCell>{item.distanceKm > 0 ? `${item.distanceKm} km` : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.source || 'Location'}
                          size="small"
                          color={sourceChipColor(item.source)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.mode || 'Web'}
                          size="small"
                          color={item.mode === 'Mobile' ? 'secondary' : 'primary'}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        {item.latitude}, {item.longitude}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredReport.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </>
      ) : (
        <>
          {viewMode === 'route' && routeLoading && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading road route...</Typography>
            </Paper>
          )}
          <Paper sx={{ height: 500, overflow: 'hidden', borderRadius: 2 }}>
            {validLocations.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">No location data to display on map</Typography>
              </Box>
            ) : (
              <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }} key={`${mapCenter[0]}-${mapCenter[1]}-${viewMode}`}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {validLocations.map((item, idx) => (
                  <Marker
                    key={idx}
                    position={[item.latitude, item.longitude]}
                    icon={createNumberedIcon(idx + 1)}
                  >
                    <Popup>
                      <strong>#{idx + 1} — {item.locationName}</strong><br />
                      Arrival: {new Date(item.arrivalTime).toLocaleTimeString()}<br />
                      {item.departureTime && <>Departure: {new Date(item.departureTime).toLocaleTimeString()}<br /></>}
                      Duration: {formatDuration(item.durationMinutes)}<br />
                      {item.distanceKm > 0 && <>Distance to next: {item.distanceKm} km<br /></>}
                      Source: {item.source} | Mode: {item.mode}<br />
                      <small>{item.latitude}, {item.longitude}</small>
                    </Popup>
                  </Marker>
                ))}
                {viewMode === 'route' && routeData ? (
                  <Polyline
                    positions={routeData.coordinates}
                    pathOptions={{ color: '#d32f2f', weight: 4, opacity: 0.85 }}
                  />
                ) : polylinePositions.length > 1 ? (
                  <Polyline
                    positions={polylinePositions}
                    pathOptions={{ color: '#9c27b0', weight: 3, dashArray: '10, 6' }}
                  />
                ) : null}
              </MapContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default LocationReportPage;
